import type { AvailabilityProduct } from '@travelity/api/services';
import { AgeBands } from '@travelity/api';
import { CardList, formatValue } from '@travelity/form';
import { Box, Chip, Stack } from '@mui/material';
import { Tag } from '@travelity/ui';
import { useCallback, useEffect, useMemo, useState } from 'react';
import {
    FormProvider,
    useForm,
    useFormContext,
    useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import type {
    PackageBookingFormValues,
    PackageBookingItemMeta,
    SelectedProductOption,
} from '../../package-form.helpers';
import { buildPackageItemsMeta } from '../../package-form.helpers';
import {
    PackageProductAddOptionsDialog,
    type PackageSelectedProductOptionCard,
} from './PackageProductAddOptionsDialog';
import {
    getSelectedOptionPrice,
    formatOptionPrice,
    toProductOptionCard,
} from './helpers';

interface PackageProductOptionsCardFormValues {
    productOptionCards: PackageSelectedProductOptionCard[];
}

interface PackageProductOptionsProps {
    selectedEvent: AvailabilityProduct;
}

const PackageProductOptions = ({
    selectedEvent,
}: PackageProductOptionsProps) => {
    const { t } = useTranslation('product');
    const { control, setValue } = useFormContext<PackageBookingFormValues>();
    const [productOptionDialog, setProductOptionDialog] = useState<{
        item?: PackageSelectedProductOptionCard;
        callback: (item: PackageSelectedProductOptionCard) => void;
    } | null>(null);

    const cardListMethods = useForm<PackageProductOptionsCardFormValues>({
        defaultValues: {
            productOptionCards: [],
        },
    });

    const selectedTime =
        useWatch({
            control,
            name: 'selectedTime',
        }) || '';
    const watchedSelectedPax = useWatch({
        control,
        name: 'pax',
    });
    const watchedSelectedProductOptionsByItemKey = useWatch({
        control,
        name: 'selectedOptionsByItemKey',
    });

    const packageItemsMeta = useMemo(
        () => buildPackageItemsMeta(selectedEvent),
        [selectedEvent]
    );
    const selectedPax = useMemo(
        () => watchedSelectedPax || {},
        [watchedSelectedPax]
    );
    const selectedProductOptionsByItemKey = useMemo(
        () => watchedSelectedProductOptionsByItemKey || {},
        [watchedSelectedProductOptionsByItemKey]
    );

    const availableProducts = useMemo(
        () =>
            packageItemsMeta.filter(
                (item) =>
                    String(item.time) === selectedTime &&
                    item.optionsCatalog.length > 0
            ),
        [packageItemsMeta, selectedTime]
    );

    const availableProductByKey = useMemo(
        () =>
            availableProducts.reduce<Record<string, PackageBookingItemMeta>>(
                (acc, item) => {
                    acc[item.key] = item;
                    return acc;
                },
                {}
            ),
        [availableProducts]
    );

    const selectedProductOptionCards = useMemo(
        () =>
            Object.entries(selectedProductOptionsByItemKey).flatMap(
                ([itemKey, selectedProductOptions]) =>
                    selectedProductOptions
                        .map((selectedProductOption) =>
                            toProductOptionCard(
                                itemKey,
                                selectedProductOption,
                                availableProductByKey
                            )
                        )
                        .filter(
                            (card): card is PackageSelectedProductOptionCard =>
                                !!card
                        )
            ),
        [availableProductByKey, selectedProductOptionsByItemKey]
    );

    useEffect(() => {
        cardListMethods.reset({
            productOptionCards: selectedProductOptionCards,
        });
    }, [cardListMethods, selectedProductOptionCards]);

    const canAddMore = useMemo(
        () =>
            availableProducts.some((product) => {
                const selectedOptionIds = (
                    selectedProductOptionsByItemKey[product.key] || []
                ).map(
                    (selectedProductOption) => selectedProductOption.optionId
                );

                return product.optionsCatalog.some(
                    (productOption) =>
                        !selectedOptionIds.includes(productOption.id)
                );
            }),
        [availableProducts, selectedProductOptionsByItemKey]
    );

    const upsertSelectedProductOption = useCallback(
        (
            currentCard: PackageSelectedProductOptionCard | undefined,
            nextCard: PackageSelectedProductOptionCard
        ) => {
            if (currentCard) {
                const currentOptions =
                    selectedProductOptionsByItemKey[currentCard.itemKey] || [];
                setValue(
                    `selectedOptionsByItemKey.${currentCard.itemKey}` as const,
                    currentOptions.filter(
                        (selectedProductOption) =>
                            selectedProductOption.optionId !==
                            currentCard.optionId
                    ),
                    { shouldDirty: true }
                );
            }

            const nextOptions =
                selectedProductOptionsByItemKey[nextCard.itemKey] || [];
            const nextSelectedProductOption: SelectedProductOption = {
                optionId: nextCard.optionId,
                pax: nextCard.pax,
            };

            setValue(
                `selectedOptionsByItemKey.${nextCard.itemKey}` as const,
                [
                    ...nextOptions.filter(
                        (selectedProductOption) =>
                            selectedProductOption.optionId !== nextCard.optionId
                    ),
                    nextSelectedProductOption,
                ],
                {
                    shouldDirty: true,
                }
            );
        },
        [selectedProductOptionsByItemKey, setValue]
    );

    const onProductOptionAdd = useCallback(
        (callback: (item: PackageSelectedProductOptionCard) => void) => {
            setProductOptionDialog({
                callback: (nextCard) => {
                    upsertSelectedProductOption(undefined, nextCard);
                    callback(nextCard);
                },
            });
        },
        [upsertSelectedProductOption]
    );

    const onProductOptionEdit = useCallback(
        (
            item: Record<string, any>,
            callback: (item: Record<string, any>) => void
        ) => {
            const currentCard = item as PackageSelectedProductOptionCard;
            setProductOptionDialog({
                item: currentCard,
                callback: (nextCard) => {
                    upsertSelectedProductOption(currentCard, nextCard);
                    callback(nextCard);
                },
            });
        },
        [upsertSelectedProductOption]
    );

    const handleProductOptionRemove = useCallback(
        (item: Record<string, any>) => {
            const selectedProductOptionCard =
                item as PackageSelectedProductOptionCard;
            const currentOptions =
                selectedProductOptionsByItemKey[
                    selectedProductOptionCard.itemKey
                ] || [];

            setValue(
                `selectedOptionsByItemKey.${selectedProductOptionCard.itemKey}` as const,
                currentOptions.filter(
                    (selectedProductOption) =>
                        selectedProductOption.optionId !==
                        selectedProductOptionCard.optionId
                ),
                { shouldDirty: true }
            );
        },
        [selectedProductOptionsByItemKey, setValue]
    );

    if (!availableProducts.length) return null;

    return (
        <>
            <FormProvider {...cardListMethods}>
                <CardList
                    renderHeader={({ item }) => (
                        <Box component="span">{item.productName}</Box>
                    )}
                    renderContent={({ item }) => (
                        <Stack direction="row">
                            <Stack
                                gap={1}
                                px={1}
                                py={1}
                                flexGrow={2}
                                minWidth={0}
                            >
                                <Box
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        color: 'text.primary',
                                        overflow: 'hidden',
                                        textOverflow: 'ellipsis',
                                        whiteSpace: 'nowrap',
                                    }}
                                >
                                    {item.productOptionName}
                                    {` (${item?.productOptionType})`}
                                </Box>
                                <Stack direction="row" gap={1} flexWrap="wrap">
                                    {Object.values(AgeBands).map(
                                        (ageBand) =>
                                            !!item.pax[ageBand] && (
                                                <Tag
                                                    key={`${item.id}:${ageBand}`}
                                                    label={t(ageBand)}
                                                    values={[item.pax[ageBand]]}
                                                />
                                            )
                                    )}
                                    {!Object.values(item.pax).some(Boolean) && (
                                        <Box
                                            sx={{
                                                fontSize: '12px',
                                                color: 'text.secondary',
                                            }}
                                        >
                                            {formatValue(item.pax) || '0 pax'}
                                        </Box>
                                    )}
                                    <Chip
                                        size="small"
                                        label={formatOptionPrice(
                                            item.pricing,
                                            item.pax
                                        )}
                                        sx={{
                                            ml: 'auto',
                                            color: getSelectedOptionPrice(
                                                item.pricing,
                                                item.pax
                                            )?.total
                                                ? 'secondary.main'
                                                : 'success.main',
                                            border: '1px solid',
                                            borderColor: getSelectedOptionPrice(
                                                item.pricing,
                                                item.pax
                                            )?.total
                                                ? 'secondary.main'
                                                : 'success.main',
                                            fontWeight: 400,
                                            fontSize: '12px',
                                            '& > .MuiChip-label': {
                                                px: '6px',
                                                py: '4px',
                                            },
                                            bgcolor: getSelectedOptionPrice(
                                                item.pricing,
                                                item.pax
                                            )?.total
                                                ? 'rgba(85, 181, 207, 0.2)'
                                                : 'rgba(44, 172, 96, 0.20)',
                                        }}
                                    />
                                </Stack>
                            </Stack>
                        </Stack>
                    )}
                    disableAdding={!canAddMore}
                    disableReorder
                    noEmptyState
                    name="productOptionCards"
                    addButtonText="Add Product Option"
                    customAdd={onProductOptionAdd}
                    customEdit={onProductOptionEdit}
                    onItemRemove={handleProductOptionRemove}
                />
            </FormProvider>

            <PackageProductAddOptionsDialog
                open={!!productOptionDialog}
                selectedPax={selectedPax}
                availableProducts={availableProducts}
                availableProductByKey={availableProductByKey}
                selectedProductOptionsByItemKey={
                    selectedProductOptionsByItemKey
                }
                editingProductOption={productOptionDialog?.item}
                onClose={() => setProductOptionDialog(null)}
                onConfirm={(nextCard) => {
                    productOptionDialog?.callback(nextCard);
                    setProductOptionDialog(null);
                }}
            />
        </>
    );
};

export default PackageProductOptions;
